/* RYZE 3D bottle slide: mounts the viewer (assets/ryze-3d.js, three.js) only when the slide is in view, pauses it out of
   view, follows the 50 ml / 100 ml variant through the theme's variant-change event, falls back to the packshot when
   WebGL is missing or the module fails. Draco GLBs with the decoder by URL. The Agentic Lab, 2026-10-18 (Part55).
   2026-10-19 (owner order): THE LAST SLIDE of the gallery. The first photo opens the page and is the LCP; this element
   stays last (never is-active, never scrolls the slider), mounts only in view and after the load event, and warms the
   HTTP cache for the customer who swipes to the end (THE IDLE WARM-UP, prefetchWhenIdle). */
(function () {
  if (customElements.get('ryze-3d-slide')) return;
  var REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // THE DESKTOP GATE (W-PERFFIX, Sawy's yes 30 Oct 2026): from 990 px the stage is in view at load, so it used to mount the
  // viewer by itself; it now rests on its poster (the viewer's own resting frame) until the visitor reaches for it
  // (pointerenter / pointerdown on the stage, focusin on the stage or the gallery). Phones keep the in-view mount, and so
  // does a touch tablet at 990+ (no hover: its first touch must turn the bottle, the refuter's P3), hence (hover: hover).
  var DESK = window.matchMedia ? window.matchMedia('(min-width: 990px) and (hover: hover)') : null;

  // W-PERFFIX (30 Oct 2026): failIfMajorPerformanceCaveat refuses a context the browser could only give on a software
  // rasteriser (no GPU, a blocklisted GPU: PageSpeed's servers drew 1-4 s per frame and the page stopped answering,
  // PAGE_HUNG). That visitor keeps the poster, the fallback picture. The probe's context is released at once, so the probe
  // never holds one of the browser's few WebGL contexts.
  function webglOk() {
    try {
      if (!window.WebGL2RenderingContext) return false;
      var c = document.createElement('canvas');
      var opts = { failIfMajorPerformanceCaveat: true };
      var gl = c.getContext('webgl2', opts) || c.getContext('webgl', opts);
      if (!gl) return false;
      var lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
      return true;
    } catch (e) { return false; }
  }

  class Ryze3DSlide extends HTMLElement {
    connectedCallback() {
      if (this._booted) return;
      this._booted = true;
      this.canvas = this.querySelector('.ryze-3d__canvas');
      this.poster = this.querySelector('.ryze-3d__poster');
      this.size = this.dataset.size === '100' ? '100' : '50';
      this.viewer = null; this.loading = false; this.visible = false; this.idleTimer = 0;
      this.releasePosterLater();  // dev2.14-perf: THE PHONE HOLD (the poster's 1x1 placeholder src), before any exit so the fallback gets its poster
      // THE DESKTOP GATE: from 990 px no WebGL context at all (not even the probe) until the visitor reaches for the stage
      this.gated = !!(this.canvas && DESK && DESK.matches);
      if (!this.canvas || (!this.gated && !webglOk())) { this.classList.add('is-fallback'); return; }
      this.io = new IntersectionObserver(this.onIntersect.bind(this), { threshold: 0.05 });
      this.io.observe(this);
      this.ro = new ResizeObserver(() => { if (this.viewer) this.viewer.resize(); });
      this.ro.observe(this);
      // Dawn's pubsub: `subscribe` is a function declaration (a window property); PUB_SUB_EVENTS is a top-level const (not one)
      if (typeof subscribe === 'function') {
        var evName = (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) || 'variant-change';
        this.unsubscribe = subscribe(evName, this.onVariant.bind(this));
      }
      this.stayLast();          // the server renders it last; this only repairs a move made by anything else
      this.prefetchWhenIdle();  // the bytes the viewer will need, after the load event, in idle time
      if (this.gated) this.armDeskGate();
    }
    // THE DESKTOP GATE's door. The first pointerenter or pointerdown on the stage, or the first focusin on the stage or
    // anywhere in its gallery (a keyboard visitor tabbing into the media; the stage itself holds nothing focusable), probes
    // WebGL and mounts at once, in the same task (the module, the model and the decoder are already in the HTTP cache from
    // THE IDLE WARM-UP). The poster and the canvas share one box (assets/ryze-3d.css: both absolute, inset 0), so the
    // handover on the first drawn frame moves and resizes nothing. A window narrowed under 990 px before any reach takes
    // the phone's in-view mount instead.
    armDeskGate() {
      var self = this;
      var gallery = this.closest('media-gallery');
      var go = function () { self.ungate(); };
      var onMedia = function () { if (!DESK.matches) self.ungate(); };
      this.addEventListener('pointerenter', go);
      this.addEventListener('pointerdown', go);
      this.addEventListener('focusin', go);
      if (gallery) gallery.addEventListener('focusin', go);
      if (DESK.addEventListener) DESK.addEventListener('change', onMedia); else if (DESK.addListener) DESK.addListener(onMedia);
      this._gateOff = function () {
        self.removeEventListener('pointerenter', go);
        self.removeEventListener('pointerdown', go);
        self.removeEventListener('focusin', go);
        if (gallery) gallery.removeEventListener('focusin', go);
        if (DESK.removeEventListener) DESK.removeEventListener('change', onMedia); else if (DESK.removeListener) DESK.removeListener(onMedia);
      };
    }
    ungate() {
      if (!this.gated) return;
      this.gated = false;
      if (this._gateOff) { this._gateOff(); this._gateOff = null; }
      if (!this.isConnected) return;
      if (!webglOk()) { this.noGL = true; this.classList.add('is-fallback'); return; }
      if (DESK && DESK.matches) { if (!this.viewer && !this.loading) this.mount(); }
      else if (this.visible && !this.viewer && !this.loading) this.mountWhenIdle();
    }
    // a DOM move (Dawn's variant re-render reorders the slides; stayLast appends this one back) fires disconnect then connect
    // in the same task: the teardown waits one microtask and runs only when the node is really gone, so the WebGL context,
    // the parsed models and the live frame all survive a size switch
    disconnectedCallback() {
      var self = this;
      queueMicrotask(function () { if (!self.isConnected) self.teardown(); });
    }
    teardown() {
      if (this.io) this.io.disconnect();
      if (this.ro) this.ro.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      if (this.viewer) { this.viewer.destroy(); this.viewer = null; }
      if (this._gateOff) { this._gateOff(); this._gateOff = null; }
      this._booted = false;
    }
    onIntersect(entries) {
      var on = entries[0].isIntersecting;
      this.visible = on;
      if (on && !this.viewer && !this.loading && !this.gated && !this.noGL) this.mountWhenIdle();
      else if (this.viewer) { if (on) this.viewer.resume(); else this.viewer.pause(); }
    }
    // the last slide is never the page's first paint (the first photo is): the viewer mounts when the slide is in view, after
    // the load event and at the first idle moment (1.5 s at most), so it never competes with the first photo. A customer who
    // swipes to the end before the load event sees the poster (the viewer's own resting frame) until then.
    mountWhenIdle() {
      var self = this;
      if (this._idleArmed) return; this._idleArmed = true;
      var go = function () { if (self.visible && !self.viewer && !self.loading && !self.gated) self.mount(); else self._idleArmed = false; };
      var after = function () { if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 1500 }); else setTimeout(go, 600); };
      if (document.readyState === 'complete') after(); else window.addEventListener('load', after, { once: true });
    }
    // THE IDLE WARM-UP (2026-10-19): after the load event and in idle time, the module, the selected size's model and the
    // Draco decoder go into the HTTP cache at low priority (fetched and read, never run), so the swipe to the end goes live from
    // the cache. Once per page; skipped when the slide is already in view and will mount by itself (the mount downloads them
    // itself; THE DESKTOP GATE's stage is in view but waits for the visitor, so it is warmed), when the viewer exists, on
    // Save-Data and on 2g. The viewer still prefetches the other size and compiles it once the bottle is live.
    prefetchWhenIdle() {
      if (Ryze3DSlide._warmed) return; Ryze3DSlide._warmed = true;
      var self = this;
      var c = navigator.connection;
      if (c && (c.saveData || /2g$/.test(String(c.effectiveType || '')))) return;
      var run = function () {
        if ((self.visible && !self.gated) || self.viewer || self.loading || typeof fetch !== 'function') return;
        if (self.noGL) return;
        [self.dataset.module, self.getAttribute('data-src-' + self.size), self.dataset.dracoWrapper, self.dataset.dracoWasm].forEach(function (u) {
          if (!u) return;
          try { fetch(u, { priority: 'low', credentials: 'same-origin' }).then(function (r) { return r.arrayBuffer(); }).catch(function () {}); } catch (e) {}
        });
      };
      var after = function () { if (window.requestIdleCallback) requestIdleCallback(run, { timeout: 4000 }); else setTimeout(run, 1500); };
      if (document.readyState === 'complete') after(); else window.addEventListener('load', after, { once: true });
    }
    onVariant(ev) {
      var v = ev && ev.data && ev.data.variant;
      if (!v) return;
      // every variant re-render prepends the variant's image, makes it active and scrolls the slider to it (Dawn's own
      // behaviour, kept): the 3D only makes sure it is still the last slide, whether or not the size changed
      this.stayLast();
      var size = /100/.test(String(v.title || '')) ? '100' : '50';
      if (size === this.size) return;
      this.size = size; this.dataset.size = size;
      if (this.viewer) this.viewer.load(this.dataset.scent, size).catch(this.fail.bind(this));
      else this.setPoster(size);
    }
    // dev2.14-perf (2026-10-30): THE PHONE HOLD. The <img> ships a 1x1 GIF and the p45 poster in data-ryze-p45 (the desktop w21
    // <source> is untouched); it gets its real src when the slide comes within half a row width of the slider's view (one swipe
    // ahead: with three slides the 3D is two swipes away at rest, measured 716 px right of a 358 px row at 390), or after
    // the load event at the first idle moment (2.5 s at most). setPoster (a size switch, or the fallback) writes the src itself,
    // so the release only fills a src that is still the placeholder.
    releasePosterLater() {
      var img = this.poster;
      var want = img && img.getAttribute('data-ryze-p45');
      if (!want) return;
      var io = null, done = false;
      var release = function () {
        if (done) return; done = true;
        if (io) io.disconnect();
        if (/^data:/.test(img.getAttribute('src') || '')) img.src = want;
      };
      var scroller = this.closest('.slider');
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(function (es) {
          for (var i = 0; i < es.length; i += 1) if (es[i].isIntersecting) { release(); return; }
        }, scroller ? { root: scroller, rootMargin: '0px 50% 0px 50%' } : { rootMargin: '300px 0px' });
        io.observe(this);
      }
      var after = function () { if (window.requestIdleCallback) requestIdleCallback(release, { timeout: 2500 }); else setTimeout(release, 1200); };
      if (document.readyState === 'complete') after(); else window.addEventListener('load', after, { once: true });
    }
    // the poster of a size: data-poster-<size>-p45 (phone, 4:5) and -w21 (desktop, 2:1), the viewer's own resting frames
    setPoster(size) {
      if (!this.poster) return;
      var p45 = this.getAttribute('data-poster-' + size + '-p45'), w21 = this.getAttribute('data-poster-' + size + '-w21');
      var src = this.poster.closest('picture') && this.poster.closest('picture').querySelector('source');
      if (src && w21) src.srcset = w21;
      if (p45) this.poster.src = p45;
    }
    // the 3D is the last slide (owner order 19 Oct). Dawn's variant re-render sorts the list to the server's order, which
    // already ends with it, so this is a repair only: it never touches is-active and never scrolls (Dawn shows the variant's
    // image, as on any product). It runs after Dawn's own setActiveMedia timeout, so the last word is still ours.
    stayLast() {
      var li = this.closest('li'); if (!li) return;
      var ul = li.parentElement; if (!ul) return;
      var self = this;
      setTimeout(function () {
        if (!li.isConnected || li.parentElement !== ul || ul.lastElementChild === li) return;
        ul.appendChild(li);
        // the slider's page count and arrows read the slides in DOM order
        var gal = self.closest('media-gallery');
        if (gal && gal.elements && gal.elements.viewer && gal.elements.viewer.resetPages) gal.elements.viewer.resetPages();
        if (self.viewer) self.viewer.resize();
      }, 0);
    }
    mount() {
      var self = this;
      this.loading = true;
      import(this.dataset.module).then(function (mod) {
        self.viewer = mod.createRyzeViewer({
          stage: self, canvas: self.canvas,
          modelUrl: function (scent, size) { return self.getAttribute('data-src-' + size); },   // data-src-50 has no dataset camelCase form
          // the Draco decoder by full URL (Shopify Files renames uploads; the loader's '<path>draco_decoder.wasm' convention cannot hold)
          draco: (self.dataset.dracoWrapper && self.dataset.dracoWasm) ? { 'draco_wasm_wrapper.js': self.dataset.dracoWrapper, 'draco_decoder.wasm': self.dataset.dracoWasm } : null,
          scent: self.dataset.scent, size: self.size,
          spin: false,   // on the product page the bottle rests facing the customer; drag turns it (the slow idle turn is for the owner's call)
          // no text on the stage (owner order 18 Oct): the poster is the viewer's own resting frame, so the handover to the
          // live canvas happens on the first drawn frame and reads as nothing at all
          onFirstFrame: function () {
            self.classList.add('is-live');
            if (!self.dataset.liveMs) self.dataset.liveMs = String(Math.round(performance.now()));   // QA: ms from navigation to the first live frame
          },
          // QA: the other size's shader programs compiled in idle time (W-FIX 18 Oct), so the first size switch does not freeze
          onWarm: function (w) { self.dataset.warmMs = String(w.ms); self.dataset.warmFrameMs = String(w.frameMs); },
        });
        self.loading = false;
        if (!self.visible) self.viewer.pause();
        if (self.viewer.ready) self.viewer.ready.catch(self.fail.bind(self));
      }).catch(this.fail.bind(this));
    }
    fail(err) {
      this.loading = false;
      if (window.console) console.warn('ryze-3d-slide: fallback', err);
      if (this.viewer) { try { this.viewer.destroy(); } catch (e) {} this.viewer = null; }
      this.classList.remove('is-live'); this.classList.add('is-fallback');
      this.setPoster(this.size);
    }
  }
  customElements.define('ryze-3d-slide', Ryze3DSlide);
})();
